/**
 * Twins — items the game draws identically — for the Glamour Reader and the
 * `/glamour` bot card (spec G3–G5, docs/superpowers/specs/2026-09-27-glamour-reader-design.md).
 *
 * A `.chara` file stores a model, not an item, and one model often stands for
 * a whole family: Dated Hempen Coif and two Hempen Coifs, Curtana Zenith and
 * its Replica, Lord's and Lady's Yukata. api-worker answers the family (the
 * row it names the family by, the named alternates, and the in-game rules
 * grouped by rule set); this module decides which twin a list names and how a
 * row reads.
 *
 * Default name: a twin that passes the in-game check for this file — it takes
 * the file's dyes, can be a glamour, and allows this tribe and gender —
 * preferring a dyeable one (GPOSERS reminders, March 2026: "use a perfectly
 * identical dyeable version instead of an undyeable one"), then one any Grand
 * Company can wear, then one that is not Dated, then one with a known
 * acquisition line, then more dye channels, then the lowest row. When none
 * passes, the lowest judged row that has an acquisition line, else the lowest
 * judged row (every row when the worker sent no rules).
 * Only twins the answer NAMES are candidates: a list can't print an item it
 * has no name for.
 *
 * Pure, and generic over the name shape so each caller keeps its own.
 */
import { charaTwinProblems, } from './chara-game-rules.js';
/** Every race × gender bit set: `EquipRaceCategory` row 1. */
const ANY_WEARER = 0xffff;
/** The named item and its alternates, row order, each checked against the file. */
export function charaTwinsOf(family, dyedChannel, character) {
    const members = [family, ...family.alternates];
    return members
        .map((member) => {
        const rules = family.rules?.find((g) => g.itemIds.includes(member.itemId)) ?? null;
        return {
            itemId: member.itemId,
            names: member.names,
            ...(member.acquisition ? { acquisition: member.acquisition } : {}),
            rules,
            dated: member.names.en.startsWith('Dated '),
            problems: rules ? charaTwinProblems(rules, dyedChannel, character) : [],
        };
    })
        .sort((a, b) => a.itemId - b.itemId);
}
/**
 * passes → dyeable → any Grand Company → not Dated → has an acquisition line
 * → more dye channels → lowest row; none passes → the lowest row with an
 * acquisition line among the twins the check judged (all of them when no
 * rules arrived), else the lowest of those judged twins. Among twins that already pass, a
 * twin every player can wear beats an extra channel the file doesn't use,
 * while a dyeable twin still beats an undyeable one (GPOSERS: "use a
 * perfectly identical dyeable version"). A known source is worth more to a
 * GPOSERS submission than a spare channel, but not more than those rules: a
 * blank line means no current route is known (old non-Augmented tomestone
 * gear is no longer sold) while an identical twin may still be obtainable.
 * When nothing passes, the list still names a twin a reader can obtain.
 *
 * @throws {RangeError} for an empty list — there is no twin to name.
 * `charaTwinsOf` never returns one: the named item is always a member.
 */
export function defaultCharaTwin(twins) {
    const lowest = twins[0];
    if (!lowest)
        throw new RangeError('defaultCharaTwin: no twins to choose from');
    const passing = twins.filter((t) => t.rules !== null && t.problems.length === 0);
    const hasLine = (t) => Boolean(t.acquisition);
    const channels = (t) => t.rules?.dyeCount ?? 0;
    const locked = (t) => Number((t.rules?.grandCompany ?? 0) > 0);
    const best = [...passing].sort((a, b) => Number(channels(b) > 0) - Number(channels(a) > 0) ||
        locked(a) - locked(b) ||
        Number(a.dated) - Number(b.dated) ||
        Number(hasLine(b)) - Number(hasLine(a)) ||
        channels(b) - channels(a) ||
        a.itemId - b.itemId);
    // A twin with unknown rules was never checked; named here it would read as
    // a fix (or, as the lowest row, hide the others' failures). Fall back among
    // the twins the check judged, unless it judged none.
    const judged = twins.some((t) => t.rules !== null)
        ? twins.filter((t) => t.rules !== null)
        : twins;
    return best[0] ?? judged.find(hasLine) ?? judged[0] ?? lowest;
}
/**
 * `fix`: the lowest row fails and the pick passes (a twin was named to fix
 * it). `block`: the pick fails — nothing fixes it, or the player chose a twin
 * that doesn't pass. `choice`: twins, nothing wrong. `unique`: one item.
 */
export function charaPieceTone(twins, picked) {
    if (picked.problems.length > 0)
        return 'block';
    if (twins[0] && twins[0].problems.length > 0)
        return 'fix';
    return twins.length > 1 ? 'choice' : 'unique';
}
/** A picker's chips for one twin, in the design's order. */
export function charaTwinFacts(twin, best) {
    const facts = [];
    if (twin.itemId === best.itemId && twin.problems.length === 0 && twin.rules) {
        facts.push({ key: 'best', kind: 'acc' });
    }
    const rules = twin.rules;
    if (rules) {
        facts.push({
            key: 'dye',
            kind: twin.problems.includes('dye') ? 'warn' : 'ok',
            n: rules.dyeCount,
        });
        if (rules.wearMask === null || rules.wearMask === ANY_WEARER) {
            facts.push({ key: 'anyTribe', kind: 'mute' });
        }
        else if (twin.problems.includes('wear')) {
            facts.push({ key: 'tribeNo', kind: 'warn' });
        }
        else {
            facts.push({ key: 'tribeOk', kind: 'ok' });
        }
    }
    if (twin.dated)
        facts.push({ key: 'dated', kind: 'mute' });
    if (rules && rules.grandCompany > 0) {
        facts.push({ key: 'grandCompany', kind: 'mute', n: rules.grandCompany });
    }
    if (rules && !rules.glamourable)
        facts.push({ key: 'noGlamour', kind: 'warn' });
    return facts;
}
//# sourceMappingURL=chara-twins.js.map